import type { RouteObject } from 'react-router';
import { lazy } from 'react';
import { RootLayout } from './root';
import { ErrorBoundary } from './components/ErrorBoundary';
// L'accueil et la page « introuvable » restent dans le chargement initial
// (page d'arrivée + repli 404). Les autres pages sont chargées à la demande,
// uniquement quand le visiteur s'y rend : le premier chargement est plus léger.
import { Home } from './pages/Home';
import { Placeholder } from './pages/Placeholder';

/**
 * Chaque mise en ligne renomme les fichiers de page. Un onglet resté ouvert
 * pendant une mise à jour continue de réclamer les **anciens** noms : ils
 * n'existent plus, le chargement échoue et le visiteur tombe sur la page
 * « Oups… » alors que le site fonctionne parfaitement.
 *
 * On recharge donc la page une seule fois pour repartir sur la version à jour.
 * Le drapeau en session évite toute boucle : si l'échec vient d'ailleurs (vrai
 * problème de réseau), la deuxième tentative laisse remonter l'erreur et le
 * filet de sécurité s'affiche normalement.
 */
const CLE_RECHARGEMENT = 'page-rechargee-apres-mise-a-jour';

function memoire(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.sessionStorage;
  } catch {
    // Navigation privée ou stockage refusé : on s'en passe.
    return undefined;
  }
}

function pageChargee<T>(importer: () => Promise<T>): Promise<T> {
  return importer().then(
    (page) => {
      memoire()?.removeItem(CLE_RECHARGEMENT);
      return page;
    },
    (erreur) => {
      const stockage = memoire();
      // Côté serveur, ou sans stockage : rien à tenter, l'erreur remonte.
      if (!stockage || stockage.getItem(CLE_RECHARGEMENT)) throw erreur;
      stockage.setItem(CLE_RECHARGEMENT, '1');
      window.location.reload();
      // La promesse reste en suspens : la page est en train de se recharger.
      return new Promise<T>(() => {});
    },
  );
}

const About = lazy(() =>
  pageChargee(() => import('./pages/About').then((mod) => ({ default: mod.About }))),
);
const Projects = lazy(() =>
  pageChargee(() => import('./pages/Projects').then((mod) => ({ default: mod.Projects }))),
);
const ProjectDetail = lazy(() =>
  pageChargee(() =>
    import('./pages/ProjectDetail').then((mod) => ({ default: mod.ProjectDetail })),
  ),
);
const IglooProjectDetail = lazy(() =>
  pageChargee(() =>
    import('./pages/IglooProjectDetail').then((mod) => ({ default: mod.IglooProjectDetail })),
  ),
);
const RafaelloProjectDetail = lazy(() =>
  pageChargee(() =>
    import('./pages/RafaelloProjectDetail').then((mod) => ({ default: mod.RafaelloProjectDetail })),
  ),
);
const LgmProjectDetail = lazy(() =>
  pageChargee(() =>
    import('./pages/LgmProjectDetail').then((mod) => ({ default: mod.LgmProjectDetail })),
  ),
);
const ShakeDuMoment = lazy(() =>
  pageChargee(() =>
    import('./pages/ShakeDuMoment').then((mod) => ({ default: mod.ShakeDuMoment })),
  ),
);
const ShakeDetail = lazy(() =>
  pageChargee(() => import('./pages/ShakeDetail').then((mod) => ({ default: mod.ShakeDetail }))),
);
const Contact = lazy(() =>
  pageChargee(() => import('./pages/Contact').then((mod) => ({ default: mod.Contact }))),
);

export const routes: RouteObject[] = [
  {
    path: '/',
    Component: RootLayout,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <Home /> },
      { path: 'about', element: <About /> },
      { path: 'projects', element: <Projects /> },
      { path: 'project/igloo/:projectId', element: <IglooProjectDetail /> },
      { path: 'project/rafaello/:projectId', element: <RafaelloProjectDetail /> },
      { path: 'project/lgm/:projectId', element: <LgmProjectDetail /> },
      { path: 'project/:category/:projectId', element: <ProjectDetail /> },
      { path: 'shake-du-moment', element: <ShakeDuMoment /> },
      { path: 'shake/:shakeId', element: <ShakeDetail /> },
      { path: 'contact', element: <Contact /> },
      { path: '*', element: <Placeholder title="Page introuvable" /> },
    ],
  },
];
